import {
  DocumentChangeType,
  type DocumentChangeEvent,
  type IReactorClient,
} from "@powerhousedao/reactor";
import type { PHDocument } from "@powerhousedao/shared/document-model";
import type {
  FulfilledPromise,
  IDocumentCache,
  PromiseState,
  PromiseWithState,
  RejectedPromise,
} from "./types/documents.js";

export function addPromiseState<T>(promise: Promise<T>): PromiseWithState<T> {
  if ("status" in promise) {
    return promise as PromiseWithState<T>;
  }

  const promiseWithState = promise as PromiseWithState<T>;
  promiseWithState.status = "pending";
  promiseWithState.then(
    (value) => {
      promiseWithState.status = "fulfilled";
      (promiseWithState as FulfilledPromise<T>).value = value;
    },
    (reason) => {
      promiseWithState.status = "rejected";
      (promiseWithState as RejectedPromise<T>).reason = reason;
      // Re-throw to preserve unhandled rejection behavior
      // This allows React's error boundaries to catch the error
      throw reason;
    },
  );

  return promiseWithState;
}

export function readPromiseState<T>(
  promise: Promise<T> | PromiseWithState<T>,
): PromiseState<T> {
  return "status" in promise ? promise : { status: "pending" };
}

/**
 * Resolves an array of document promises using `Promise.allSettled`,
 * returning only the fulfilled values. A single missing/rejected
 * document does not poison the whole batch. Rejected entries are
 * logged at warn level so developers can see dangling references.
 */
function fulfilledOnly(promises: Promise<PHDocument>[]): Promise<PHDocument[]> {
  return Promise.allSettled(promises).then((results) => {
    const documents: PHDocument[] = [];
    for (const result of results) {
      if (result.status === "fulfilled") {
        documents.push(result.value);
      } else {
        console.warn(
          "[DocumentCache] Skipped unavailable document:",
          result.reason,
        );
      }
    }
    return documents;
  });
}

/**
 * Document cache implementation that uses the new ReactorClient API.
 *
 * This cache subscribes to document change events via IReactorClient.subscribe()
 * and automatically updates the cache when documents are created, updated, or deleted.
 */
export class DocumentCache implements IDocumentCache {
  private documents = new Map<string, PromiseWithState<PHDocument>>();
  private batchPromises = new Map<
    string,
    { promises: Promise<PHDocument>[]; promise: Promise<PHDocument[]> }
  >();
  private listeners = new Map<string, (() => void)[]>();
  private unsubscribe: (() => void) | null = null;

  constructor(private client: IReactorClient) {
    this.unsubscribe = client.subscribe({}, (event: DocumentChangeEvent) => {
      this.handleDocumentChange(event);
    });
  }

  private handleDocumentChange(event: DocumentChangeEvent): void {
    if (event.type === DocumentChangeType.Deleted) {
      const documentId = event.context?.childId;
      if (documentId) {
        this.handleDocumentDeleted(documentId);
      }
    } else if (event.type === DocumentChangeType.Updated) {
      for (const doc of event.documents) {
        this.handleDocumentUpdated(doc.header.id).catch(console.warn);
      }
    }
  }

  private handleDocumentDeleted(documentId: string): void {
    const listeners = this.listeners.get(documentId);
    this.documents.delete(documentId);
    this.invalidateBatchesContaining(documentId);
    if (listeners) {
      listeners.forEach((listener) => listener());
    }
    this.listeners.delete(documentId);
  }

  private async handleDocumentUpdated(documentId: string): Promise<void> {
    if (this.documents.has(documentId)) {
      await this.get(documentId, true);
      const listeners = this.listeners.get(documentId);
      if (listeners) {
        listeners.forEach((listener) => listener());
      }
    }
  }

  private invalidateBatchesContaining(documentId: string): void {
    for (const key of this.batchPromises.keys()) {
      if (key.split(",").includes(documentId)) {
        this.batchPromises.delete(key);
      }
    }
  }

  get(id: string, refetch?: boolean): Promise<PHDocument> {
    const currentData = this.documents.get(id);
    if (currentData) {
      if (currentData.status === "pending") {
        return currentData;
      }
      if (!refetch) {
        return currentData;
      }
    }

    const documentPromise = this.client.get(id);
    this.documents.set(id, addPromiseState(documentPromise));
    return documentPromise;
  }

  getBatch(ids: string[]): Promise<PHDocument[]> {
    const key = ids.join(",");
    const cached = this.batchPromises.get(key);

    const hasDeletedDocuments = ids.some((id) => !this.documents.has(id));
    const currentPromises = ids.map((id) => this.get(id));

    if (hasDeletedDocuments) {
      const batchPromise = fulfilledOnly(currentPromises);
      this.batchPromises.set(key, {
        promises: currentPromises,
        promise: batchPromise,
      });
      return batchPromise;
    }

    if (cached) {
      const samePromises = currentPromises.every(
        (p, i) => p === cached.promises[i],
      );
      if (samePromises) {
        return cached.promise;
      }
    }

    const states = currentPromises.map((p) =>
      readPromiseState(p as PromiseWithState<PHDocument>),
    );
    const allSettled = states.every((s) => s.status !== "pending");

    if (allSettled) {
      const values = states
        .filter(
          (s): s is { status: "fulfilled"; value: PHDocument } =>
            s.status === "fulfilled",
        )
        .map((s) => s.value);
      const batchPromise = Promise.resolve(values) as PromiseWithState<
        PHDocument[]
      >;
      batchPromise.status = "fulfilled";
      (batchPromise as FulfilledPromise<PHDocument[]>).value = values;

      this.batchPromises.set(key, {
        promises: currentPromises,
        promise: batchPromise,
      });
      return batchPromise;
    }

    if (cached) {
      return cached.promise;
    }

    const batchPromise = fulfilledOnly(currentPromises);
    this.batchPromises.set(key, {
      promises: currentPromises,
      promise: batchPromise,
    });
    return batchPromise;
  }

  subscribe(id: string | string[], callback: () => void): () => void {
    const ids = Array.isArray(id) ? id : [id];
    for (const docId of ids) {
      const listeners = this.listeners.get(docId) ?? [];
      this.listeners.set(docId, [...listeners, callback]);
    }
    return () => {
      for (const docId of ids) {
        const listeners = this.listeners.get(docId) ?? [];
        this.listeners.set(
          docId,
          listeners.filter((listener) => listener !== callback),
        );
      }
    };
  }

  /**
   * Disposes of the cache and unsubscribes from document change events.
   */
  dispose(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }
}
